import React, { useState, useEffect } from 'react'
import { graphql } from 'gatsby'
import { PrismicRichText } from '@prismicio/react'
import SEO from '../components/SEO'
import Layout from '../components/Layout'
import CtaLink from '../components/ContentBlocks/CtaLink'
import { HcpdBackArrow } from '../components/Icons'
import { RegionContainer } from '../components/styles/Utility.styles'
import * as Styled from './styles/ProductDetailPage.styles'
import RelatedProducts from '../components/RelatedProducts'
import AdSlot from '../components/AdSlot'
import AdHead from '../components/AdHead'

export const Head = ({ pageContext }) => {
  return (
    <AdHead adsData={pageContext.adsData} targets={pageContext.pageTargets} />
  )
}

const BackButton = () => (
  <RegionContainer>
    <Styled.BackButtonContainer to="/seal-of-recognition/">
      <HcpdBackArrow />
      <span>Product Directory</span>
    </Styled.BackButtonContainer>
  </RegionContainer>
)

const ProductDetailPage = ({ data, pageContext }) => {
  const { prismicProduct, prismicGeneralSiteSettings } = data
  const { data: productData } = prismicProduct
  const { data: siteSettings } = prismicGeneralSiteSettings
  const { id, relatedProducts } = pageContext
  const {
    product_name,
    product_company,
    product_image,
    product_description,
    product_detail_page_description,
    product_link,
  } = productData
  const { product_detail_page_middle_banner_google_ad } = siteSettings

  // Updated rich text check to use product_description.richText if product_detail_page_description.richText is empty.
  const productDetailRichText = product_detail_page_description?.richText
  const productDescriptionRichText =
    productDetailRichText && productDetailRichText.length > 0
      ? productDetailRichText
      : product_description.richText

  const productImageUrl =
    product_image?.thumbnails?.detail_page?.url || product_image.url

  return (
    <Layout>
      <SEO
        seo={{
          title: product_name?.text ?? '',
        }}
      />
      <BackButton />
      <RegionContainer>
        <Styled.HeroRegion>
          <Styled.ProductTitle>
            <PrismicRichText
              field={product_name.richText}
              components={{
                heading2: ({ children, key }) => <h1 key={key}>{children}</h1>,
              }}
            />
          </Styled.ProductTitle>
          <Styled.ProductDetailRegion>
            <Styled.ProductImageContainer>
              <Styled.ProductImage
                src={productImageUrl}
                alt={product_image.alt}
              />
            </Styled.ProductImageContainer>
            <Styled.TextColumn>
              <Styled.ProductCompany>
                {product_company.document.data.product_company}
              </Styled.ProductCompany>
              <Styled.ProductDescriptionBox>
                <PrismicRichText field={productDescriptionRichText} />
                <CtaLink text="Purchase This Product" link={product_link} />
              </Styled.ProductDescriptionBox>
            </Styled.TextColumn>
          </Styled.ProductDetailRegion>
        </Styled.HeroRegion>
      </RegionContainer>
      <RegionContainer>
        <Styled.Divider />
      </RegionContainer>

      <Styled.MiddleGoogleAdContainer>
        <AdSlot
          divId={`div-${product_detail_page_middle_banner_google_ad.document.data.ad_slot_name}`}
        />
      </Styled.MiddleGoogleAdContainer>

      <RegionContainer>
        <RelatedProducts products={relatedProducts} />
      </RegionContainer>
    </Layout>
  )
}

export const query = graphql`
  query ProductDetailPageQuery($id: String) {
    prismicGeneralSiteSettings {
      data {
        product_detail_page_middle_banner_google_ad {
          document {
            ... on PrismicGoogleAdTag {
              id
              data {
                ad_slot_name
              }
            }
          }
        }
      }
    }
    prismicProduct(id: { eq: $id }) {
      id
      data {
        product_name {
          text
          richText
        }
        product_company {
          document {
            ... on PrismicProductCompany {
              id
              data {
                product_company
              }
            }
          }
        }
        product_image {
          url
          alt
          thumbnails {
            detail_page {
              url
            }
          }
        }
        product_description {
          richText
        }
        product_detail_page_description {
          richText
        }
        product_link {
          uid
          type
          url
          link_type
          id
        }
      }
    }
  }
`

export default ProductDetailPage
